import { multiScreenService } from '@/lib/multiScreen/multiScreenService';
import { useCallback, useEffect, useState } from 'react';
export function useMultiScreen() {
    const [settings, setSettings] = useState(multiScreenService.getSettings());
    const [screens, setScreens] = useState([]);
    const [isSupported] = useState(() => multiScreenService.isSupported());
    const [supportMessage] = useState(() => multiScreenService.getBrowserSupportMessage());
    useEffect(() => {
        // Subscribe to settings changes
        const unsubscribe = multiScreenService.subscribe(() => {
            setSettings(multiScreenService.getSettings());
            setScreens(multiScreenService.getScreens());
        });
        // Try to re-acquire permission on mount if we previously had it
        if (settings.hasPermission) {
            multiScreenService.tryReacquirePermission().then((success) => {
                if (success) {
                    setScreens(multiScreenService.getScreens());
                }
            });
        }
        return unsubscribe;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only effect; settings.hasPermission read once on init
    const requestPermission = useCallback(async () => {
        const granted = await multiScreenService.requestPermission();
        if (granted) {
            setScreens(multiScreenService.getScreens());
        }
        return granted;
    }, []);
    const setEnabled = useCallback((enabled) => {
        multiScreenService.setEnabled(enabled);
    }, []);
    const setViewerScreen = useCallback((index) => {
        multiScreenService.setViewerScreen(index);
    }, []);
    const setOpenFullscreen = useCallback((fullscreen) => {
        multiScreenService.updateSettings({ openFullscreen: fullscreen });
    }, []);
    const openStudyInViewer = useCallback(async (studyId, opts) => {
        return multiScreenService.openCockpit({
            studyId,
            seriesId: opts?.seriesId,
            caseId: opts?.caseId,
            primary: 'viewer',
        });
    }, []);
    // New monitor configuration methods
    const getMonitorConfig = useCallback((screenIndex) => {
        return multiScreenService.getMonitorConfig(screenIndex);
    }, []);
    const updateMonitorRole = useCallback((screenIndex, role, enabled) => {
        multiScreenService.updateMonitorRole(screenIndex, role, enabled);
    }, []);
    const updateMonitorLayout = useCallback((screenIndex, layout) => {
        multiScreenService.updateMonitorLayout(screenIndex, layout);
    }, []);
    const resetMonitorConfig = useCallback((screenIndex) => {
        multiScreenService.resetMonitorConfig(screenIndex);
    }, []);
    const identifyMonitors = useCallback(() => {
        multiScreenService.identifyMonitors();
    }, []);
    return {
        // State
        isSupported,
        supportMessage,
        settings,
        screens,
        // Actions
        requestPermission,
        setEnabled,
        setViewerScreen,
        setOpenFullscreen,
        openStudyInViewer,
        // Monitor configuration
        getMonitorConfig,
        updateMonitorRole,
        updateMonitorLayout,
        resetMonitorConfig,
        identifyMonitors,
    };
}
